import { useEffect, useMemo, useState } from "react";
import { Modal, Skeleton } from "antd";
import { useSelector } from "react-redux";
import Chart from "react-google-charts";
import { ModalXIcon } from "../../../assets/icons";
import dayjsTZ from "../../../utils/dayjs";
import { getShiftChart } from "./utils/gcharts";
import { ShiftCard } from "./ProgressShift";
import { paySubFactory } from "../../Pagat/PaySub/employeeCalc/employeeSubFactory";
import useIpAddress from "./hooks/use-IpAddress";
import MondayButton from "../../commonComponents/MondayButton/MondayButton";
import "./TimeSheet.scss";
import {
  anyIntegrityTampered,
  isIntegrityTampered,
} from "../../../utils/integrityStatus";

function InfoModal({
  info,
  isVisible,
  toggleVisible,
  totalHours,
  shiftsConfig,
}) {
  const geoData = useIpAddress();

  const { employees } = useSelector((state) => state.employeesList);
  const { isDarkMode } = useSelector((state) => state.usersList);
  const { programFields } = useSelector((state) => state.programFields);

  const [dayShift, setDayShift] = useState(null);
  const [moreDetails, setMoreDetails] = useState(true);

  useEffect(() => {
    if (!!info && !!shiftsConfig) {
      const testTemp = info?.map((el) => ({
        ...el,
        dayType: "Regular",
        clockInDate:
          el?.clockingCategory === "midnightReset"
            ? dayjsTZ(el?.clockInDate).add(1, "days")
            : el?.clockInDate,
        clockOutDate:
          el?.clockingCategory === "midnightReset"
            ? dayjsTZ(el?.clockOutDate).add(1, "days")
            : el?.clockOutDate,
      }));
      const resu = paySubFactory({
        type: "slicing",
        employee: testTemp[0]?.employeeId,
        clockings: testTemp,
        momentInstance: null,
        programFields: programFields,
      });
      if (resu?.calculations?.length > 0) {
        let toSet = [];
        resu?.calculations?.forEach((calcArr) => {
          calcArr.forEach((calc) => {
            toSet.push(calc);
          });
        });
        toSet = toSet
          ?.map((r) => {
            const source =
              (Array.isArray(info) &&
                info.find((s) => s?.clockingId && s.clockingId === r?.clockingId)) ||
              (Array.isArray(info) && info[0]) ||
              {};
            return {
              ...r,
              integrityOk: source.integrityOk ?? r.integrityOk,
              integrityStatus: source.integrityStatus ?? r.integrityStatus,
              integrityCode: source.integrityCode ?? r.integrityCode,
              fillim: dayjsTZ(r?.clockInDate).format("HH:mm"),
              mbarimi: dayjsTZ(r?.clockOutDate).format("HH:mm"),
              duration: dayjsTZ(r?.clockOutDate)
                .diff(r?.clockInDate, "hours", true)
                .toFixed(2),
              applicableShift: getEmployeeShift(
                employees,
                r?.employeeId,
                shiftsConfig,
              ),
            };
          })
          .sort((a, b) => a.clockOutDate - b.clockInDate);
        setDayShift(toSet);
      }
    }
  }, [info, shiftsConfig, employees, programFields]);

  const shiftTampered = anyIntegrityTampered(info) || anyIntegrityTampered(dayShift);

  const chartData = useMemo(
    () => getShiftChart(dayShift, geoData),
    [dayShift, geoData],
  );

  const chartOptions = useMemo(
    () => ({
      timeline: {
        showRowLabels: true,
        colorByRowLabel: true,
        rowLabelStyle: {
          fontName: "Open Sans",
          fontSize: 12,
          bold: true,
          color: isDarkMode ? "#f4f4f5" : "#0f172a",
        },
        barLabelStyle: {
          fontName: "Open Sans",
          fontSize: 11,
          color: isDarkMode ? "#fafafa" : "#0f172a",
        },
      },
      hAxis: {
        format: "HH:mm",
        textStyle: {
          color: isDarkMode ? "#e4e4e7" : "#0f172a",
          bold: true,
        },
      },
      backgroundColor: {
        fill: isDarkMode ? "#1c1c1c" : "#f8fafc",
        stroke: isDarkMode ? "#3f3f46" : "#cbd5e1",
        strokeWidth: 0,
      },
      legend: "none",
      allowHtml: true,
      tooltip: { isHtml: true, trigger: "focus" },
    }),
    [isDarkMode],
  );

  const themeClass = isDarkMode ? "dark darkHeader" : "lightHeader";
  const contrastClass = isDarkMode ? "is-dark" : "is-light";
  const dateLabel =
    dayShift?.length > 0
      ? dayjsTZ(dayShift[0]?.clockInDate).format("DD/MM/YYYY")
      : null;

  return (
    <Modal
      centered
      className={`progress-shift-modal ${moreDetails ? "more-details" : ""} ${themeClass} ${contrastClass}`}
      wrapClassName={`progress-shift-modal-wrap ${themeClass} ${contrastClass}`}
      rootClassName={`progress-shift-modal-root ${contrastClass}`}
      title="Më shumë informacion për turnin"
      open={isVisible}
      onOk={toggleVisible}
      width={Math.min(1100, typeof window !== "undefined" ? window.innerWidth - 32 : 1100)}
      zIndex={10000}
      onCancel={toggleVisible}
      destroyOnHidden
      closeIcon={<ModalXIcon />}
      footer={[
        <MondayButton
          key="close"
          className="mondayButtonRed"
          onClick={toggleVisible}
        >
          Mbyll
        </MondayButton>,
      ]}
    >
      {dayShift !== null && chartData !== null ? (
        <>
          <p className="content-label">
            Oraret e regjistruara për datën{" "}
            {dateLabel ? <strong>{dateLabel}</strong> : null}
          </p>
          {shiftTampered ? (
            <div
              className="integrity-tamper-banner"
              data-testid="integrity-tamper-banner"
              role="alert"
            >
              Integrity cipher mismatch — clocking data failed originality
              verification
              {Array.isArray(info) &&
              info.find((s) => isIntegrityTampered(s))?.integrityCode
                ? ` (${
                    info.find((s) => isIntegrityTampered(s)).integrityCode
                  })`
                : ""}
            </div>
          ) : null}

          <div className={`static-shift-tellers ${contrastClass}`}>
            <Chart
              chartType="Timeline"
              data={chartData}
              width="100%"
              height="320px"
              options={chartOptions}
            />
          </div>

          <div className="more-details-cont">
            <h3 className="hours-label">
              Totali: <span>{totalHours || 0}</span> orë
            </h3>
            <button
              type="button"
              className="more-details-shift"
              onClick={() => setMoreDetails((v) => !v)}
            >
              {!moreDetails ? "Më shumë detaje" : "Më pak detaje"}
            </button>
          </div>

          {moreDetails ? (
            <div className="shift-cards-wrapper">
              {dayShift?.map((el, idx) => (
                <ShiftCard key={`${el?.clockInDate}-${idx}`} info={el} />
              ))}
            </div>
          ) : null}
        </>
      ) : (
        <Skeleton active paragraph={{ rows: 6 }} />
      )}
    </Modal>
  );
}

export default InfoModal;

const getEmployeeShift = (employees, employeeId, shiftsConfig) => {
  let result = [];
  let test = [];

  if (Array.isArray(employees) && !!employeeId) {
    result = employees.find(
      (el) => el?.employeeId === employeeId,
    )?.employeeShiftId;
    result?.map((el) => {
      let temp = shiftsConfig?.find((shift) => shift?.shiftId === el);
      if (!!temp) {
        test.push(temp);
      }
    });

    return test;
  }
  return test;
};
